// Personnes et droits d'accès du manuel de procédures.
//
// Le prototype lisait window.APP_DATA (collaborateurs fictifs). Ici les
// personnes sont les vrais comptes MyALFA, fournis par l'écran via
// procSetPeople() à chaque rendu — un registre de module plutôt qu'un
// contexte : ces fonctions sont aussi appelées hors composants (le store
// attribue chaque modification à l'utilisateur courant).

let USERS = [];
let ME = null;

export function procSetPeople(users, me) {
  USERS = Array.isArray(users) ? users : [];
  ME = me || null;
}

/* ---------- Identité ---------- */

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
// Ensemble de mots, ordre indifférent : "HOARAU Anthony" == "Anthony Hoarau".
// Les comptes MyALFA n'ont pas de convention stable sur name/lastName
// (parfois NOM/Prénom, parfois Prénom/NOM) — on ne s'y fie donc pas.
const tokens = (s) => norm(s).split(/[^a-z0-9]+/).filter(Boolean).sort().join(" ");

// Auteurs de la graine, identifiés par les codes du prototype. Ils sont
// rattachés au vrai compte par le nom ; à défaut (compte pas encore chargé),
// on affiche ces valeurs de repli.
const LEGACY = {
  ha: { name: "HOARAU", lastName: "Anthony" },
  bk: { name: "BARDEUR", lastName: "Kéren" },
  bl: { name: "BEGUE", lastName: "LAURENCIE" },
  he: { name: "HOARAU", lastName: "Elisa" },
};

const isCaps = (s) => !!s && s === s.toUpperCase() && s !== s.toLowerCase();
const capitalize = (s) => s.toLowerCase().replace(/(^|[-\s'])(\p{L})/gu, (m, a, b) => a + b.toUpperCase());

function personOf(u) {
  const a = (u.name || "").trim();
  const b = (u.lastName || "").trim();
  let prenom = null;
  let nom = null;
  if (isCaps(a) && !isCaps(b)) { nom = a; prenom = b; }
  else if (isCaps(b) && !isCaps(a)) { nom = b; prenom = a; }
  const brut = `${a} ${b}`.trim();
  return {
    id: u.id == null ? "" : String(u.id),
    // "HOARAU Anthony" : liste déroulante, tri
    name: nom ? `${nom.toUpperCase()} ${prenom}`.trim() : brut,
    // "Anthony Hoarau" : texte courant
    display: nom ? `${prenom} ${capitalize(nom)}`.trim() : brut,
    // Initiales dans l'ordre des champs du compte (name puis lastName) : c'est
    // ce qu'affiche déjà MyALFA (Home.js), "HA" pour HOARAU Anthony.
    initials: ((a[0] || "") + (b[0] || "")).toUpperCase() || "?",
    role: u.role || "",
    poste: u.poste || "",
  };
}

const isActive = (u) => !u.status || u.status === "Actif";

// Annuaire des personnes, y compris l'utilisateur connecté même si la liste
// des comptes n'est pas encore arrivée (rechargement direct de la page).
export function procPeople() {
  const list = USERS.filter(isActive);
  if (ME && !list.some((u) => String(u.id) === String(ME.id))) list.push(ME);
  return list
    .map(personOf)
    .sort((x, y) => x.name.localeCompare(y.name, "fr", { sensitivity: "base" }));
}

// Code du prototype -> identifiant du vrai compte, quand il est connu.
export const procNormId = (id) => {
  if (id == null) return id;
  const s = String(id);
  const legacy = LEGACY[s];
  if (!legacy) return s;
  const key = tokens(`${legacy.name} ${legacy.lastName}`);
  const u = USERS.find((x) => tokens(`${x.name || ""} ${x.lastName || ""}`) === key);
  return u ? String(u.id) : s;
};

export const procMe = () => (ME ? String(ME.id) : null);

export const procWho = (rawId) => {
  const id = procNormId(rawId);
  if (id == null) return { id: "", name: "—", display: "—", initials: "?", role: "", poste: "" };
  const u = USERS.find((x) => String(x.id) === id) || (ME && String(ME.id) === id ? ME : null);
  if (u) return personOf(u);
  const legacy = LEGACY[String(rawId)];
  if (legacy) return { ...personOf({ id: String(rawId), ...legacy }), id: String(rawId) };
  return { id, name: String(id), display: String(id), initials: "?", role: "", poste: "" };
};

export const procName = (id) => procWho(id).display;

/* ---------- Droits d'accès ---------- */

export const PROC_LEVELS = {
  read: { lab: "Lecture", r: 1, desc: "Peut consulter" },
  edit: { lab: "Modification", r: 2, desc: "Peut consulter et modifier" },
  manage: { lab: "Gestion", r: 3, desc: "Peut modifier et gérer les accès" },
};

// Groupes définis sur le rôle MyALFA (le prototype se fondait sur un libellé
// de poste libre). "Direction" = administrateurs et experts-comptables : les
// "responsables de site" du prototype n'ont pas d'équivalent dans les rôles.
export const PROC_GROUPS = [
  { id: "g:all", name: "Tout le cabinet", match: () => true },
  { id: "g:direction", name: "Direction", match: (p) => ["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(p.role) },
  { id: "g:compta", name: "Pôle comptable", match: (p) => ["COLLABORATEUR", "SUPERVISEUR", "ASSISTANT"].includes(p.role) },
  { id: "g:paie", name: "Pôle paie", match: (p) => p.role === "SOCIAL" || /paie/i.test(p.poste) },
  { id: "g:juri", name: "Pôle juridique", match: (p) => p.role === "JURIDIQUE" || /juridique/i.test(p.poste) },
  { id: "g:stag", name: "Stagiaires", match: (p) => /stagiaire/i.test(p.poste) },
];

const procGroup = (id) => PROC_GROUPS.find((g) => g.id === id);

export const procMembers = (who) => {
  if (who.startsWith("g:")) {
    const g = procGroup(who);
    return g ? procPeople().filter(g.match).map((p) => p.id) : [];
  }
  return [procNormId(who.replace(/^u:/, ""))];
};

export const procAclOf = (db, page) =>
  (page && page.acl) || ((db.spaces.find((s) => s.id === (page ? page.spaceId : null)) || {}).acl) || [];

export const procLevelIn = (acl, uid) =>
  acl.reduce((m, e) => (procMembers(e.who).includes(procNormId(uid)) ? Math.max(m, PROC_LEVELS[e.level].r) : m), 0);

export const procCan = (db, page, need, uid = procMe()) =>
  procLevelIn(procAclOf(db, page), uid) >= PROC_LEVELS[need].r;

export const procCanSpace = (db, spaceId, need, uid = procMe()) =>
  procLevelIn((db.spaces.find((s) => s.id === spaceId) || {}).acl || [], uid) >= PROC_LEVELS[need].r;

export const procWhoName = (who) =>
  who.startsWith("g:") ? (procGroup(who) || {}).name : procWho(who.replace(/^u:/, "")).name;

export const procIsRestricted = (acl) => !acl.some((e) => e.who === "g:all");
